import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Center,
  Text,
  useToast,
  Flex,
  HStack,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getEnhancedDashboardMetrics, getBookingTrendData, getServicePopularityData } from '../services/dashboardMetrics';
import { getAllServiceTypes, ServiceType } from '../services/firestore';
import { getWaitingListMetrics, WaitingListEntry } from '../services/waitingListMetrics';
import { Link } from 'react-router-dom';
import { FiMapPin } from 'react-icons/fi';

// Cast icon as React.ElementType to fix TypeScript issues
const FiMapPinIcon = FiMapPin as React.ElementType;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const Dashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<any>(null);
  const [bookingTrend, setBookingTrend] = useState<any[]>([]);
  const [servicePopularity, setServicePopularity] = useState<any[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [waitingListMetrics, setWaitingListMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [dashboardMetrics, trendData, popularityData, serviceTypesData, waitingListData] = await Promise.all([
          getEnhancedDashboardMetrics(),
          getBookingTrendData(7),
          getServicePopularityData(),
          getAllServiceTypes(),
          getWaitingListMetrics().catch(err => {
            console.warn('Error fetching waiting list metrics:', err);
            return null;
          }),
        ]);

        setMetrics(dashboardMetrics);
        setBookingTrend(trendData);
        setServicePopularity(popularityData);
        setServiceTypes(serviceTypesData);
        setWaitingListMetrics(waitingListData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast({
          title: 'Error loading dashboard',
          description: 'Failed to fetch dashboard data',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [toast]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'in_progress':
        return 'blue';
      case 'confirmed':
        return 'yellow';
      case 'pending':
        return 'orange';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const formatWaitingListAddress = (entry: WaitingListEntry) => {
    const parts = [];
    if (entry.street) parts.push(entry.street);
    if (entry.city) parts.push(entry.city);
    if (entry.state) parts.push(entry.state);
    if (entry.zipCode) parts.push(entry.zipCode);
    return parts.length > 0 ? parts.join(', ') : 'Address not available';
  };

  // Helper to format cents to dollars
  const formatAmount = (amount: number | undefined) => `$${((amount ?? 0) / 100).toFixed(2)}`;

  const getCustomerName = (booking: any) => {
    const c = booking.customer || {};
    if (c.firstName && c.lastName) return `${c.firstName} ${c.lastName}`;
    if (c.firstName) return c.firstName;
    if (c.lastName) return c.lastName;
    if (booking.customerName) return booking.customerName;
    if (c.email) return c.email;
    return 'Unknown';
  };

  if (loading || !metrics) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  // Prepare data for status distribution pie chart
  const statusDistribution = Object.entries(metrics.bookingsByStatus).map(([status, count]) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: count,
  }));

  return (
    <Box>
      <Heading 
        size={{ base: 'md', md: 'lg' }} 
        mb={6}
        pl={{ base: '60px', lg: 0 }}
        pr={{ base: 4, lg: 0 }}
      >
        Dashboard Overview
      </Heading>
      
      <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} mb={6}>
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Total Users</StatLabel>
          <StatNumber>{metrics.totalUsers.toLocaleString()}</StatNumber>
          <StatHelpText>Registered users</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Active Bookings</StatLabel>
          <StatNumber>{metrics.activeBookings}</StatNumber>
          <StatHelpText>In progress</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Total Revenue</StatLabel>
          <StatNumber>{formatAmount(metrics.totalRevenue)}</StatNumber>
          <StatHelpText>All time</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Completion Rate</StatLabel>
          <StatNumber>{metrics.completionRate}%</StatNumber>
          <StatHelpText>Service completion</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Technicians</StatLabel>
          <StatNumber>{metrics.technicians}</StatNumber>
          <StatHelpText>Active</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Services</StatLabel>
          <StatNumber>{metrics.services}</StatNumber>
          <StatHelpText>Available</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Pending</StatLabel>
          <StatNumber color="orange.500">{metrics.pendingBookings}</StatNumber>
          <StatHelpText>Awaiting assignment</StatHelpText>
        </Stat>
        
        <Stat p={4} bg="white" borderRadius="lg" boxShadow="sm">
          <StatLabel>Completed</StatLabel>
          <StatNumber color="green.500">{metrics.completedBookings}</StatNumber>
          <StatHelpText>Successfully completed</StatHelpText>
        </Stat>
      </Grid>
      
      {/* Charts Grid */}
      <Grid templateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={6} mb={6}>
        {/* Booking Trend Chart */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={4}>Booking Trends (Last 7 Days)</Heading>
          {bookingTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={bookingTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip 
                  formatter={(value: any, name: string) => {
                    if (name === 'revenue') return `$${value.toFixed(2)}`;
                    return value;
                  }}
                />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="bookings" stroke="#8884d8" name="Bookings" />
                <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#82ca9d" name="Revenue ($)" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Text color="gray.500" textAlign="center" py={4}>
              No data available
            </Text>
          )}
        </Box>

        {/* Service Popularity Chart */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={4}>Service Popularity</Heading>
          {servicePopularity.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={servicePopularity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="bookings" fill="#8884d8" name="Bookings" />
                <Bar dataKey="revenue" fill="#82ca9d" name="Revenue ($)" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Text color="gray.500" textAlign="center" py={4}>
              No data available
            </Text>
          )}
        </Box>

        {/* Status Distribution Pie Chart */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" mb={4}>Booking Status Distribution</Heading>
          {statusDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Text color="gray.500" textAlign="center" py={4}>
              No data available
            </Text>
          )}
        </Box>
      </Grid>
      
      {/* Recent Bookings Table */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
        <Heading size="md" mb={4}>Recent Bookings</Heading>
        {metrics.recentBookings.length > 0 ? (
          <Box 
            overflowX="auto" 
            overflowY="auto"
            maxH="calc(100vh - 400px)"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Table variant="simple" minW="100%">
            <Thead>
              <Tr>
                <Th>Customer</Th>
                <Th>Service</Th>
                <Th>Date</Th>
                <Th>Status</Th>
                <Th>Amount</Th>
              </Tr>
            </Thead>
            <Tbody>
              {metrics.recentBookings.map((booking: any) => (
                <Tr key={booking.id}>
                  <Td>{getCustomerName(booking)}</Td>
                  <Td>{booking.serviceType}</Td>
                  <Td>{formatDate(booking.scheduledTime)}</Td>
                  <Td>
                    <Badge colorScheme={getStatusColor(booking.status)}>
                      {booking.status?.replace('_', ' ') || 'Unknown'}
                    </Badge>
                  </Td>
                  <Td>{formatAmount(booking.amount)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          </Box>
        ) : (
          <Text color="gray.500" textAlign="center" py={4}>
            No recent bookings found
          </Text>
        )}
      </Box>

      {/* Waiting List Section */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" mt={6}>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Heading size="md">Waiting List</Heading>
          <Link to="/waiting-list">
            <HStack as="button" color="blue.500" _hover={{ color: 'blue.600' }}>
              <Text fontSize="sm" fontWeight="medium">View All</Text>
              <FiMapPinIcon />
            </HStack>
          </Link>
        </Flex>
        {waitingListMetrics && waitingListMetrics.totalEntries > 0 ? (
          <>
            <HStack mb={4} spacing={4}>
              <Stat p={3} bg="blue.50" borderRadius="md" flex="1">
                <StatLabel fontSize="sm">Total Entries</StatLabel>
                <StatNumber fontSize="lg">{waitingListMetrics.totalEntries}</StatNumber>
              </Stat>
              <Stat p={3} bg="orange.50" borderRadius="md" flex="1">
                <StatLabel fontSize="sm">Top City</StatLabel>
                <StatNumber fontSize="lg">
                  {Object.entries(waitingListMetrics.entriesByCity)
                    .sort(([, a], [, b]) => (b as number) - (a as number))[0]?.[0] || 'N/A'}
                </StatNumber>
              </Stat>
              <Stat p={3} bg="green.50" borderRadius="md" flex="1">
                <StatLabel fontSize="sm">Geographic Clusters</StatLabel>
                <StatNumber fontSize="lg">{waitingListMetrics.geographicClusters.length}</StatNumber>
              </Stat>
            </HStack>
            {waitingListMetrics.recentEntries.length > 0 ? (
              <Box 
                overflowX="auto" 
                overflowY="auto"
                maxH="300px"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
              >
                <Table variant="simple" minW="100%" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Address</Th>
                      <Th>City</Th>
                      <Th>State</Th>
                      <Th>Date Added</Th>
                      <Th>Reason</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {waitingListMetrics.recentEntries.map((entry: WaitingListEntry) => (
                      <Tr key={entry.id}>
                        <Td>{entry.street || 'N/A'}</Td>
                        <Td>{entry.city || 'N/A'}</Td>
                        <Td>{entry.state || 'N/A'}</Td>
                        <Td>{formatDate(entry.createdAt)}</Td>
                        <Td>
                          <Badge colorScheme="orange" fontSize="xs">
                            {entry.reason || 'Out of service area'}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Text color="gray.500" textAlign="center" py={4}>
                No waiting list entries found
              </Text>
            )}
          </>
        ) : (
          <Text color="gray.500" textAlign="center" py={4}>
            No waiting list entries yet
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard; 